import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { message, Modal } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type FinanceProductDetailsWithPermissionsDataFragment } from '../../../api/fragments/FinanceProductDetailsWithPermissionsData';
import {
    DeleteFinanceProductDocument,
    type DeleteFinanceProductMutation,
    type DeleteFinanceProductMutationVariables,
} from '../../../api/mutations/deleteFinanceProduct';
import {
    UpdateFinanceProductDocument,
    type UpdateFinanceProductMutation,
    type UpdateFinanceProductMutationVariables,
} from '../../../api/mutations/updateFinanceProduct';
import { type FinanceProductSettings, VehicleKind } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import { type FinanceProductFormValues } from './FinanceProductForm';
import mapDataToFormValues from './FinanceProductForm/helpers/mapDataToFormValues';
import mapFormValuesToPayload from './FinanceProductForm/helpers/mapFormValuesToPayload';
import {
    finderVehicleParameterKey,
    variantParameterKey,
} from './FinanceProductForm/tables/LeasePurchaseTable/useVehicleReferenceParameterRecords';
import useFinanceProductFormValidator, {
    validateInterestRateRandomTable,
} from './FinanceProductForm/useProductFormValidator';
import useLocalVehicles, { useFinderVehicles } from './FinanceProductForm/VehicleAssignmentTab/useVehicles';
import Inner from './Inner';

export type FinanceProductDetailsInnerPageProps = { product: FinanceProductDetailsWithPermissionsDataFragment };

const FinanceProductDetailsInnerPage = ({ product }: FinanceProductDetailsInnerPageProps) => {
    const { t } = useTranslation(['financeProductDetails']);
    const navigate = useNavigate();
    const productId = product.id;

    const apolloClient = useApolloClient();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: product.versioning,
        moduleId: product.module.id,
    });

    const company = useCompany(true);
    const vehicles = useLocalVehicles({ companyId: company?.id });
    const finderVehicles = useFinderVehicles({ companyId: company?.id });

    const permissions = useMemo(
        () => ({
            update: hasPermissions(product.permissions, [permissionKind.updateFinanceProduct]),
            delete: hasPermissions(product.permissions, [permissionKind.deleteFinanceProduct]),
        }),
        [product.permissions]
    );
    const validate = useFinanceProductFormValidator();

    const onSubmit = useHandleError<FinanceProductFormValues>(
        async values => {
            const invalid = validateInterestRateRandomTable(values, t);
            if (invalid) {
                message.warning(invalid);

                return;
            }

            // submitting message
            message.loading({
                content: t('financeProductDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            await apolloClient
                .mutate<UpdateFinanceProductMutation, UpdateFinanceProductMutationVariables>({
                    mutation: UpdateFinanceProductDocument,
                    variables: {
                        id: productId,
                        settings: mapFormValuesToPayload(values, { vehicles, finderVehicles }),
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('financeProductDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate('/admin/financeProducts');
        },
        [t, apolloClient, productId, vehicles, finderVehicles, navigate]
    );

    const deleteFinanceProduct = useCallback(() => {
        Modal.confirm({
            title: t('financeProductDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('financeProductDetails:deleteModal.content'),
            okText: t('financeProductDetails:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('financeProductDetails:deleteModal.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('financeProductDetails:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    await apolloClient
                        .mutate<DeleteFinanceProductMutation, DeleteFinanceProductMutationVariables>({
                            mutation: DeleteFinanceProductDocument,
                            variables: { id: productId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    // show success
                    message.success({
                        content: t('financeProductDetails:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    // then go back to list
                    navigate('/admin/financeProducts');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, productId, navigate, t]);

    const initialValues = useMemo((): FinanceProductFormValues => {
        const common: Array<
            keyof Pick<
                FinanceProductSettings,
                | 'displayName'
                | 'legalName'
                | 'identifier'
                | 'bankReferenceIdentifier'
                | 'bankId'
                | 'webCalcSettingId'
                | 'deriveMethod'
                | 'description'
                | 'isActive'
                | 'order'
                | 'period'
                | 'term'
                | 'type'
                | 'variantSuiteIds'
                | 'vehicleReferenceParameters'
            >
        > = [
            'displayName',
            'legalName',
            'identifier',
            'bankReferenceIdentifier',
            'bankId',
            'webCalcSettingId',
            'deriveMethod',
            'description',
            'isActive',
            'order',
            'period',
            'term',
            'type',
            'variantSuiteIds',
            'vehicleReferenceParameters',
        ];

        const mapFinanceProduct = () => {
            switch (product.__typename) {
                case 'LocalHirePurchase':
                case 'LocalDeferredPrincipal':
                    return {
                        ...pick(
                            [...common, 'basedOn', 'calculationMode', 'payment', 'downPayment', 'loan', 'interestRate'],
                            product
                        ),
                    };

                case 'LocalLease':
                    return {
                        ...pick([...common, 'deposit'], product),
                        lease: {
                            ...product.lease,
                            table: [
                                ...product.lease.table,
                                ...product.lease.vehicleFilterParameterTable.map(value => ({
                                    ...value,
                                    variantSuiteId:
                                        value.type === VehicleKind.FinderVehicle
                                            ? finderVehicleParameterKey
                                            : variantParameterKey,
                                })),
                            ],
                        },
                    };

                case 'LocalUcclLeasing': {
                    const { insuranceFee } = product;

                    return {
                        ...pick(
                            [
                                ...common,
                                'basedOn',
                                'calculationMode',
                                'payment',
                                'downPayment',
                                'loan',
                                'interestRate',
                                'licensePlateFee',
                                'commission',
                                'monthlyPaymentFixedInterestRate',
                                'licenseAndFuelTax',
                                'displacement',
                                'taxLoss',
                            ],
                            product
                        ),
                        insuranceFee: {
                            ...insuranceFee,
                            table: [
                                ...insuranceFee.table,
                                ...insuranceFee.vehicleFilterParameterTable.map(value => ({
                                    ...value,
                                    variantSuiteId:
                                        value.type === VehicleKind.FinderVehicle
                                            ? finderVehicleParameterKey
                                            : variantParameterKey,
                                })),
                            ],
                        },
                    };
                }

                case 'LocalHirePurchaseWithBalloon': {
                    const { balloon } = product;

                    if (balloon.__typename !== 'BalloonTableSettings') {
                        return {
                            ...pick(
                                [
                                    ...common,
                                    'basedOn',
                                    'calculationMode',
                                    'payment',
                                    'downPayment',
                                    'loan',
                                    'interestRate',
                                    'balloon',
                                ],
                                product
                            ),
                        };
                    }

                    return {
                        ...pick(
                            [...common, 'basedOn', 'calculationMode', 'payment', 'downPayment', 'loan', 'interestRate'],
                            product
                        ),
                        balloon: {
                            ...balloon,
                            table: [
                                ...balloon.table,
                                ...balloon.vehicleFilterParameterTable.map(value => ({
                                    ...value,
                                    variantSuiteId:
                                        value.type === VehicleKind.FinderVehicle
                                            ? finderVehicleParameterKey
                                            : variantParameterKey,
                                })),
                            ],
                        },
                    };
                }

                case 'LocalLeasePurchase': {
                    const { residualValue } = product;

                    return {
                        ...pick(
                            [...common, 'basedOn', 'calculationMode', 'payment', 'downPayment', 'loan', 'interestRate'],
                            product
                        ),
                        residualValue: {
                            ...residualValue,
                            table: [
                                ...residualValue.table,
                                ...residualValue.vehicleFilterParameterTable.map(value => ({
                                    ...value,
                                    variantSuiteId:
                                        value.type === VehicleKind.FinderVehicle
                                            ? finderVehicleParameterKey
                                            : variantParameterKey,
                                })),
                            ],
                        },
                    };
                }

                case 'LocalHirePurchaseWithBalloonGFV':
                    return {
                        ...pick(
                            [...common, 'basedOn', 'calculationMode', 'payment', 'downPayment', 'loan', 'interestRate'],
                            product
                        ),
                        balloonGfv: {
                            ...product.balloon,
                            table: [
                                ...product.balloon.table,
                                ...product.balloon.vehicleFilterParameterTable.map(value => ({
                                    ...value,
                                    variantSuiteId:
                                        value.type === VehicleKind.FinderVehicle
                                            ? finderVehicleParameterKey
                                            : variantParameterKey,
                                })),
                            ],
                        },
                    };

                default:
                    throw new Error('Finance Product Type not implemented');
            }
        };

        const financeProduct = mapFinanceProduct();

        return mapDataToFormValues(financeProduct);
    }, [product]);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Inner
                    deleteFinanceProduct={deleteFinanceProduct}
                    handleSubmit={handleSubmit}
                    offset={offset}
                    permissions={permissions}
                    updated={updated}
                />
            )}
        </Formik>
    );
};

export default FinanceProductDetailsInnerPage;
